import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { InfoCard, CopyTextButton } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

function CodeBlock({ text }: { text: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        my: 1,
        backgroundColor: 'rgba(0,0,0,0.04)',
        borderRadius: 1,
        p: 1,
      }}
    >
      <Typography
        component="pre"
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          flex: 1,
          m: 0,
        }}
      >
        {text}
      </Typography>
      <CopyTextButton text={text} />
    </Box>
  );
}

interface NodePool {
  name: string;
  machineType?: string;
  minNodeCount?: number;
  maxNodeCount?: number;
  nodeCount?: number;
}

export function GkeClusterCard() {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  if (annotations['gcp.com/service'] !== 'gke') {
    return null;
  }

  const details = (entity.spec?.gcpDetails ?? undefined) as
    Record<string, unknown> | undefined;

  const {
    currentMasterVersion,
    releaseChannel,
    privateEndpoint,
    workloadIdentityPool,
    endpoint,
    dnsEndpoint,
    dnsEndpointAllowExternalTraffic,
    nodePools,
  } = (details ?? {}) as {
    currentMasterVersion?: string;
    releaseChannel?: string;
    privateEndpoint?: boolean;
    workloadIdentityPool?: string;
    endpoint?: string;
    dnsEndpoint?: string;
    dnsEndpointAllowExternalTraffic?: boolean;
    nodePools?: NodePool[];
  };

  const project = entity.metadata.namespace;
  const clusterName = entity.metadata.name;
  const region = annotations['gcp.com/location'];
  const getCredentialsCmd = region
    ? `gcloud container clusters get-credentials ${clusterName} --region ${region} --project ${project}${
        dnsEndpoint ? ' --dns-endpoint' : ''
      }`
    : undefined;

  return (
    <InfoCard title="GKE Cluster">
      <Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {currentMasterVersion && (
            <Chip
              label={currentMasterVersion}
              size="small"
              variant="outlined"
            />
          )}
          {releaseChannel && (
            <Chip label={releaseChannel} size="small" variant="outlined" />
          )}
          {privateEndpoint !== undefined && (
            <Chip
              label={`Private: ${privateEndpoint ? 'yes' : 'no'}`}
              size="small"
              variant="outlined"
            />
          )}
          {workloadIdentityPool && (
            <Chip
              label={`Workload Identity: ${workloadIdentityPool}`}
              size="small"
              variant="outlined"
            />
          )}
          {dnsEndpointAllowExternalTraffic !== undefined && (
            <Chip
              label={`DNS external traffic: ${
                dnsEndpointAllowExternalTraffic ? 'allowed' : 'blocked'
              }`}
              size="small"
              color={dnsEndpointAllowExternalTraffic ? 'success' : 'warning'}
              variant="outlined"
            />
          )}
        </Box>

        {getCredentialsCmd && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Connect
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Requires the gke-gcloud-auth-plugin and gcloud auth. With{' '}
              <code>--dns-endpoint</code> kubectl uses the DNS endpoint instead
              of the IP, which works from anywhere (no VPN, no IP allowlist).
            </Typography>
            <CodeBlock text={getCredentialsCmd} />
          </>
        )}

        {dnsEndpoint && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              DNS Endpoint
            </Typography>
            <CodeBlock text={dnsEndpoint} />
          </>
        )}

        {endpoint && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              IP Endpoint
            </Typography>
            <CodeBlock text={endpoint} />
          </>
        )}

        {nodePools && nodePools.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Node Pools
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Machine Type</TableCell>
                  <TableCell>Min</TableCell>
                  <TableCell>Max</TableCell>
                  <TableCell>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nodePools.map(np => (
                  <TableRow key={np.name}>
                    <TableCell>{np.name}</TableCell>
                    <TableCell>{np.machineType ?? '—'}</TableCell>
                    <TableCell>
                      {np.minNodeCount !== undefined ? np.minNodeCount : '—'}
                    </TableCell>
                    <TableCell>
                      {np.maxNodeCount !== undefined ? np.maxNodeCount : '—'}
                    </TableCell>
                    <TableCell>
                      {np.nodeCount !== undefined ? np.nodeCount : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Box>
    </InfoCard>
  );
}
